import { Injectable, inject, signal } from '@angular/core';
import { ConfirmationService } from 'primeng/api';

export type ConfirmPopupType = 'info' | 'warning' | 'success' | 'error';

export interface ConfirmPopupConfig {
  message: string;
  type?: ConfirmPopupType;
  acceptLabel?: string;
  rejectLabel?: string;
  handleSaving?: boolean;
  showAcceptButton?: boolean;
  showRejectButton?: boolean;
}

export interface ConfirmPopupState {
  isActive: boolean;
  message: string;
  type: ConfirmPopupType;
  acceptLabel: string;
  rejectLabel: string;
  target: EventTarget | null;
  handleSaving: boolean;
  showAcceptButton: boolean;
  showRejectButton: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ConfirmPopupService {
  private confirmationService = inject(ConfirmationService);
  
  // Signal-based state management
  private confirmPopupState = signal<ConfirmPopupState>({
    isActive: false,
    message: '',
    type: 'warning',
    acceptLabel: 'YES',
    rejectLabel: 'NO',
    target: null,
    handleSaving: false,
    showAcceptButton: true,
    showRejectButton: true
  });


  isSaving = signal<boolean>(false);

  // Public readonly signal for components to subscribe to
  readonly currentConfirmation = this.confirmPopupState.asReadonly();

  private currentResolve: ((value: boolean) => void) | null = null;
  private scrollListener: (() => void) | null = null;

  confirm(target: EventTarget, config: ConfirmPopupConfig): Promise<boolean> {
    return new Promise((resolve) => {
      // If there's a previous unresolved popup, reject it first
      if (this.currentResolve) {
        this.currentResolve(false);
        this.currentResolve = null;
      }

      // Store the resolve function for later use
      this.currentResolve = resolve;
      
      // Close any existing confirmation first
      if (this.confirmPopupState().isActive) {
        this.confirmationService.close();
        this.cleanup();
      }
      
      // Small delay to ensure clean state
      setTimeout(() => {
        const type = config.type || 'warning';
        
        // Update the signal state
        this.confirmPopupState.set({
          isActive: true,
          message: config.message,
          type,
          acceptLabel: config.acceptLabel || 'YES',
          rejectLabel: config.rejectLabel || 'NO',
          target,
          handleSaving: config.handleSaving || false,
          showAcceptButton: config.showAcceptButton ?? true,
          showRejectButton: config.showRejectButton ?? true
        });

        this.setupScrollListener();

        // Trigger the confirmation service
        this.confirmationService.confirm({
          target: target,
          message: config.message,
          header: this.getHeader(type),
          // No accept callback - handled directly in template
          accept: () => this.handleAccept(),
          reject: () => this.handleReject()
        });

        // Position the arrow after the popup is fully rendered and positioned by PrimeNG
        // Use requestAnimationFrame to ensure DOM has been updated
        requestAnimationFrame(() => {
          setTimeout(() => this.centerArrowOnButton(target as HTMLElement), 0);
        });
      }, 200);
    });
  }

  private cleanup(): void {
    this.confirmPopupState.update(state => ({ ...state, isActive: false }));
    this.removeScrollListener();
    
    // Remove positioned class from any popups for next time
    const popovers = document.querySelectorAll<HTMLElement>('.p-confirmpopup');
    popovers.forEach(popup => popup.classList.remove('positioned'));
  }

  private setupScrollListener(): void {
    // Remove any existing listener first
    this.removeScrollListener();
    
    // Create the scroll handler
    this.scrollListener = () => {
      this.closeAll();
    };
    
    // Add listener to window for all scroll events (including nested scrollable elements)
    window.addEventListener('scroll', this.scrollListener, true);
  }

  private removeScrollListener(): void {
    if (this.scrollListener) {
      window.removeEventListener('scroll', this.scrollListener, true);
      this.scrollListener = null;
    }
  }

  // Public method to close all confirm popups
  closeAll(): void {
    if (this.confirmPopupState().isActive) {
      this.confirmationService.close();
      this.cleanup();
      
      // Reject any pending promise
      if (this.currentResolve) {
        this.currentResolve(false);
        this.currentResolve = null;
      }
    }
  }

  acceptClick(): void {
    // Custom accept handler that doesn't close the popup
    if (this.currentResolve) {
      this.currentResolve(true);
      this.currentResolve = null;
    }
  }

  private handleAccept(): void {
    // Don't update state to keep popup open
    this.confirmPopupState.update(state => ({ ...state, isActive: false }));
    this.removeScrollListener();
    if (this.currentResolve) {
      this.currentResolve(true);
      this.currentResolve = null;
    }
  }

  private handleReject(): void {
    this.confirmPopupState.update(state => ({ ...state, isActive: false }));
    this.removeScrollListener();
    if (this.currentResolve) {
      this.currentResolve(false);
      this.currentResolve = null;
    }
  }

  private getHeader(type: ConfirmPopupType): string {
    const headerMap: Record<ConfirmPopupType, string> = {
      info: 'Information',
      warning: 'Warning',
      success: 'Success',
      error: 'Error'
    };
    return headerMap[type];
  }

  getIconName(type: ConfirmPopupType): string {
    const iconMap: Record<ConfirmPopupType, string> = {
      info: 'heroInformationCircle',
      warning: 'heroExclamationTriangle',
      success: 'heroCheckCircle',
      error: 'heroXCircle'
    };
    return iconMap[type];
  }

  getStyleClass(type: ConfirmPopupType): string {
    return `confirm-popup-${type}-message`;
  }

  private centerArrowOnButton(targetElement: HTMLElement): void {
    if (!targetElement) {
      return;
    }

    // Find the most recently opened popup
    const popovers = document.querySelectorAll<HTMLElement>('.p-confirmpopup');
    if (!popovers.length) {
      return;
    }
    
    const popup = popovers[popovers.length - 1];
    
    // Get bounding rectangles after PrimeNG has positioned the popup
    const targetRect = targetElement.getBoundingClientRect();
    const popupRect = popup.getBoundingClientRect();
    
    // Calculate the center of the button
    const buttonCenterX = targetRect.left + targetRect.width / 2;
    const arrowLeftPosition = buttonCenterX - popupRect.left;
    
    // Calculate arrow position to center on button
    const arrowWidth = 12;
    const arrowCenterOffset = arrowLeftPosition - (arrowWidth / 2) + 5;

    let offset = 0;
    let padding = 20;
    if (targetRect.left + popupRect.width > window.innerWidth) {
      offset = targetRect.width/2 - arrowWidth/2 - padding;
    } else {
      offset = padding - targetRect.width/2 + arrowWidth/2;
    }

    popup.style.removeProperty('transform');
    popup.style.setProperty('left', `${popup.getBoundingClientRect().left - offset}px`, 'important');
    popup.style.setProperty('--p-popover-arrow-left', `${arrowCenterOffset + offset}px`);
    
    // Show the popup now that positioning is complete
    setTimeout(() => {
      popup.classList.add('positioned');
    }, 100);
  }
}
